/**
 * 任务批量操作
 */
import { ElMessage } from 'element-plus'
import { taskAPI } from '@/api/task'
import type { Ref } from 'vue'
import type { Task } from '@/types/task.domain'

export function useTaskBatch(
    tasks: Ref<Task[]>,
    currentTask: Ref<Task | null>,
    total: Ref<number>,
    refreshTasks: () => Promise<void>
) {
    /**
     * 删除任务（单个）
     * 
     * 为了复用 deleteTasks 的逻辑，这里也接收 deleteTask
     * 但实际上 deleteTask 在主 Store 实现更简单，这里主要负责批量
     */

    /**
     * 删除多个任务
     */
    const deleteTasks = async (taskIds: string[]) => {
        try {
            // 这里应该调用API删除多个任务
            for (const taskId of taskIds) {
                await taskAPI.deleteTask(taskId)

                // 从列表移除
                const index = tasks.value.findIndex(t => t.taskId === taskId)
                if (index > -1) {
                    tasks.value.splice(index, 1)
                }

                if (currentTask.value && currentTask.value.taskId === taskId) {
                    currentTask.value = null
                }
            }

            // 更新总数 (近似更新，准确更新需要 refresh)
            // total.value -= taskIds.length // 暂不手动更新 total，依靠 refresh

            ElMessage.success('任务已删除')
        } catch (err: any) {
            ElMessage.error(err.message || '删除任务失败')
            throw err
        }
    }

    /**
     * 清空所有任务
     */
    const clearAllTasks = async () => {
        try {
            // 这里应该调用API清空所有任务
            // 暂时模拟清空本地
            tasks.value = []
            total.value = 0
            console.log('clearAllTasks called - 任务已清空(本地)')
            // ElMessage.success('所有任务已清空')
        } catch (err: any) {
            ElMessage.error(err.message || '清空任务失败')
            throw err
        }
    }

    /**
     * 批量取消任务
     */
    const batchCancelTasks = async (taskIds: string[]) => {
        try {
            // 这里的实现依赖于是否有批量取消 API，如果没有则循环调用
            const results = await Promise.allSettled(
                taskIds.map(taskId =>
                    // 这里无法直接调用主 store 的 cancelTask，需要调用 API
                    // 或者通过 updateTask 模拟
                    // 我们直接假设有一个 update API 或者循环使用
                    // 但 updateTask 是本地更新，这里其实需要调用 API
                    // 假设没有 batchCancel API，只能模拟
                    updateTaskStatus(taskId, 'cancelled')
                )
            )

            const successCount = results.filter(r => r.status === 'fulfilled').length
            const failCount = results.filter(r => r.status === 'rejected').length

            if (successCount > 0) {
                ElMessage.success(`成功取消 ${successCount} 个任务`)
            }
            if (failCount > 0) {
                ElMessage.warning(`${failCount} 个任务取消失败`)
            }

            await refreshTasks()
        } catch (err: any) {
            ElMessage.error(err.message || '批量取消失败')
            throw err
        }
    }

    /**
     * 批量重试任务
     */
    const batchRetryTasks = async (taskIds: string[]) => {
        try {
            const results = await Promise.allSettled(
                taskIds.map(taskId => updateTaskStatus(taskId, 'created', { progress: 0, error: undefined }))
            )

            const successCount = results.filter(r => r.status === 'fulfilled').length
            const failCount = results.filter(r => r.status === 'rejected').length

            if (successCount > 0) {
                ElMessage.success(`成功重试 ${successCount} 个任务`)
            }
            if (failCount > 0) {
                ElMessage.warning(`${failCount} 个任务重试失败`)
            }

            await refreshTasks()
        } catch (err: any) {
            ElMessage.error(err.message || '批量重试失败')
            throw err
        }
    }

    /**
     * 重试失败的任务项
     */
    const retryFailedTaskItems = async (taskId: string): Promise<boolean> => {
        try {
            await taskAPI.retryTask(taskId)
            ElMessage.success('失败的任务项已重新开始')
            await refreshTasks()
            return true
        } catch (err: any) {
            ElMessage.error(err.message || '重试失败的任务项失败')
            return false
        }
    }

    // 内部辅助：更新任务状态（本地更新）
    // 由于没有后端 updateTask API，这里直接操作本地 tasks 数组
    const updateTaskStatus = async (taskId: string, status: string, additionalUpdates: Record<string, unknown> = {}) => {
        const index = tasks.value.findIndex(t => t.taskId === taskId)
        if (index > -1) {
            tasks.value[index] = {
                ...tasks.value[index],
                status: status as Task['status'],
                updatedAt: new Date().toISOString(),
                ...additionalUpdates
            }
        }
        return Promise.resolve()
    }

    return {
        deleteTasks,
        clearAllTasks,
        batchCancelTasks,
        batchRetryTasks,
        retryFailedTaskItems
    }
}
